import { useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Menu, X, CalendarDays } from "lucide-react";

const CAL_URL = "https://cal.com/billy-sadia-gv78xs/discovery-call?overlayCalendar=true";

const NAV_LINKS = [
  { label: "What We Build", target: "modules" },
  { label: "Process", target: "process" },
  { label: "Packages", target: "packages" },
  { label: "FAQ", target: "faq" },
  { label: "Contact", path: "/contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const goToSection = (target) => {
    setOpen(false);
    if (location.pathname !== "/") navigate(`/#${target}`);
    else {
      const el = document.getElementById(target);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const goToProposal = () => {
    setOpen(false);
    if (location.pathname !== "/") navigate("/#proposal");
    else {
      const el = document.getElementById("proposal");
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // unused but keep for potential future use
  void goToProposal;

  return (
    <header
      data-testid="site-navbar"
      className={`sticky top-0 z-50 w-full transition-all duration-300 ${
        scrolled
          ? "backdrop-blur-xl bg-[#05060c]/80 border-b border-white/[0.06]"
          : "bg-transparent border-b border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8 h-16 md:h-20 flex items-center justify-between">
        <Link to="/" data-testid="navbar-logo-link" className="flex items-center gap-2.5 group">
          <span className="relative inline-flex h-9 w-9 items-center justify-center rounded-lg bg-gradient-to-br from-cyan-300 to-blue-500 text-slate-950 font-black tracking-tighter text-lg glow-cyan-soft">
            B
          </span>
          <span className="font-extrabold text-white tracking-tight text-lg">
            Brand-in-a-Box
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {NAV_LINKS.map((link) =>
            link.path ? (
              <Link
                key={link.label}
                to={link.path}
                data-testid={`navbar-link-${link.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition-colors"
              >
                {link.label}
              </Link>
            ) : (
              <button
                key={link.label}
                onClick={() => goToSection(link.target)}
                data-testid={`navbar-link-${link.label.toLowerCase()}`}
                className="px-4 py-2 text-sm font-medium text-slate-400 hover:text-white transition-colors"
              >
                {link.label}
              </button>
            ),
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            data-testid="navbar-cta-request-proposal"
            href={CAL_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold px-5 py-2 text-sm glow-cyan-soft transition-all"
          >
            <CalendarDays className="h-4 w-4" /> Book a Strategy Call
          </a>
        </div>

        <button
          data-testid="navbar-mobile-toggle"
          className="lg:hidden inline-flex items-center justify-center h-10 w-10 rounded-lg border border-white/10 text-white"
          onClick={() => setOpen((o) => !o)}
          aria-label="Toggle menu"
        >
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {open && (
        <div data-testid="navbar-mobile-menu" className="lg:hidden border-t border-white/[0.06] bg-[#05060c]/95 backdrop-blur-xl">
          <div className="px-6 py-4 flex flex-col gap-1">
            {NAV_LINKS.map((link) =>
              link.path ? (
                <Link
                  key={link.label}
                  to={link.path}
                  onClick={() => setOpen(false)}
                  data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
                  className="py-3 text-base font-medium text-slate-200"
                >
                  {link.label}
                </Link>
              ) : (
                <button
                  key={link.label}
                  onClick={() => goToSection(link.target)}
                  data-testid={`navbar-mobile-link-${link.label.toLowerCase()}`}
                  className="text-left py-3 text-base font-medium text-slate-200"
                >
                  {link.label}
                </button>
              ),
            )}
            <a
              data-testid="navbar-mobile-cta-request-proposal"
              href={CAL_URL}
              target="_blank"
              rel="noopener noreferrer"
              onClick={() => setOpen(false)}
              className="mt-3 inline-flex items-center justify-center gap-2 rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-950 font-bold py-3"
            >
              <CalendarDays className="h-4 w-4" /> Book a Strategy Call
            </a>
          </div>
        </div>
      )}
    </header>
  );
}
